import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HiCheckCircle,
  HiOutlineChartBar,
  HiOutlineColorSwatch,
  HiOutlineExternalLink,
  HiOutlineIdentification,
  HiOutlineUsers,
} from "react-icons/hi";
import { ModalShell } from "../atoms/ModalShell";
import { CustomButton } from "../atoms/CustomButton";
import BusinessCardShareButton from "../atoms/BusinessCardShareButton";
import { TrialActivationModal } from "./TrialActivationModal";
import { useTrialEligibility } from "../../hooks/promoCode/trial";

interface BusinessCardSuccessModalProps {
  show: boolean;
  onClose: () => void;
  processId: string | null;
  wasUpdate?: boolean;
}

const CARD_FEATURES = [
  { key: "saveContact", Icon: HiOutlineIdentification },
  { key: "registration", Icon: HiOutlineUsers },
  { key: "themes", Icon: HiOutlineColorSwatch },
  { key: "statistics", Icon: HiOutlineChartBar },
] as const;

export const BusinessCardSuccessModal: React.FC<
  BusinessCardSuccessModalProps
> = ({ show, onClose, processId, wasUpdate = false }) => {
  const { t } = useTranslation();
  const { eligible } = useTrialEligibility();
  const [showTrial, setShowTrial] = useState(false);

  const liveUrl = processId
    ? `${process.env.REACT_APP_HOST_URL}/business-card/${processId}`
    : null;

  const openLiveCard = () => {
    if (liveUrl) window.open(liveUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <ModalShell
        show={show}
        onClose={onClose}
        size="lg"
        title={
          <span className="flex min-w-0 items-center gap-2">
            <HiCheckCircle className="flex-shrink-0 text-green-500" size={20} />
            <span className="truncate">
              {wasUpdate
                ? t("businessCardPage.successModal.titleUpdated")
                : t("businessCardPage.successModal.title")}
            </span>
          </span>
        }
        subtitle={t("businessCardPage.successModal.subtitle") as string}
        footer={
          <CustomButton
            variant="primary"
            onClick={onClose}
            title={t("businessCardPage.successModal.done") as string}
          />
        }
      >
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {t("businessCardPage.successModal.shareTitle")}
            </h4>
            <p className="text-xs text-gray-500">
              {t("businessCardPage.successModal.shareDescription")}
            </p>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              {processId && (
                <BusinessCardShareButton
                  processId={processId}
                  variant="secondary"
                  size="md"
                  showLabel
                  className="w-full sm:w-auto"
                />
              )}
              <CustomButton
                variant="secondary"
                onClick={openLiveCard}
                disabled={!liveUrl}
                icon={<HiOutlineExternalLink size={18} />}
                title={t("businessCardPage.successModal.openLive") as string}
                className="w-full sm:w-auto"
              />
            </div>
          </section>

          {eligible && (
            <section className="flex flex-col gap-3 border-t border-gray-200 pt-5">
              <h4 className="text-sm font-semibold text-gray-900">
                {t("businessCardPage.successModal.teaserTitle")}
              </h4>
              <ul className="flex flex-col gap-3">
                {CARD_FEATURES.map(({ key, Icon }) => (
                  <li key={key} className="flex items-start gap-3">
                    <Icon
                      size={18}
                      className="mt-0.5 flex-shrink-0 text-gray-400"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {t(
                          `businessCardPage.successModal.features.${key}.title`
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t(
                          `businessCardPage.successModal.features.${key}.description`
                        )}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-500">
                {t("businessCardPage.successModal.teaserDisclaimer")}
              </p>
              <div>
                <CustomButton
                  variant="primary"
                  onClick={() => setShowTrial(true)}
                  title={t("dashboard.trialOffer.ctaShort") as string}
                  className="w-full sm:w-auto"
                />
              </div>
            </section>
          )}
        </div>
      </ModalShell>

      <TrialActivationModal
        show={showTrial}
        onClose={() => setShowTrial(false)}
        onActivated={() => {
          setShowTrial(false);
          onClose();
        }}
      />
    </>
  );
};

export default BusinessCardSuccessModal;
